import React from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGauge, faLocationDot, faCubes, faChartColumn, faUser, faBars} from "@fortawesome/free-solid-svg-icons";
import * as Constants from "../../utils/Constants";
import {cn} from "../../utils/cn";

// Dashboard and Live Tracking are mutually exclusive, not two independent permission-gated
// items — Track Point (MOBI_TRACK) has no Dashboard access (see rolePermissions.js) and gets
// Live Tracking as its one Operate tab instead, mirroring the sidebar. Every other role keeps
// Dashboard and reaches Tracking via the dashboard's Quick Actions row, as before.
const ITEMS = [
  {key: "Dashboard", label: "Dashboard", icon: faGauge, path: "/home", permission: "sidebar_dashboard"},
  {key: "Live Tracking", label: "Tracking", icon: faLocationDot, path: "/tracking", permission: "sidebar_tracking", onlyWithoutDashboard: true},
  {key: Constants.ASSETS, label: Constants.ASSETS, icon: faCubes, path: "/assets", permission: "sidebar_assets"},
  {key: Constants.REPORTS, label: Constants.REPORTS, icon: faChartColumn, path: "/reports", permission: "sidebar_reports"},
  {key: Constants.PROFILE, label: Constants.PROFILE, icon: faUser, path: "/profile", permission: "sidebar_profile"},
];

/**
 * Fixed bottom tab bar shown below `md` (768px) — the same breakpoint where
 * the sidebar becomes an off-canvas drawer. Mirrors the sidebar's top-level
 * destinations for the most-used screens, plus an always-visible "More" tab
 * (no header hamburger to go looking for) that opens that same drawer for
 * everything else it has (Users / Settings / Audit Log / Logout).
 *
 * Pinned at `bottom: var(--footer-height)` rather than the screen edge —
 * the legal footer (copyright/Terms/Privacy, see footer.css) owns the
 * true bottom edge below it.
 */
export default function BottomNav({selectedItem, onNavigate, hasPermission}) {
  const items = ITEMS.filter((item) => {
    if (item.permission && !hasPermission(item.permission)) return false;
    if (item.onlyWithoutDashboard && hasPermission("sidebar_dashboard")) return false;
    return true;
  });
  const openMore = () => document.body.classList.toggle("mobile-sidebar-open");

  return (
    <nav
      className="flex md:hidden fixed bottom-[var(--footer-height)] inset-x-0 z-bottom-nav h-[60px]
                 bg-white border-t border-ink-200 shadow-[0_-2px_8px_rgba(16,24,40,0.06)]
                 pb-[env(safe-area-inset-bottom)]"
    >
      {items.map((item) => {
        const active = selectedItem === item.key;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onNavigate(item.path, item.key)}
            className={cn(
              "flex-1 flex flex-col items-center justify-center gap-1 text-2xs font-medium",
              active ? "text-accent-500" : "text-ink-500"
            )}
          >
            <FontAwesomeIcon icon={item.icon} className="text-lg" />
            <span>{item.label}</span>
          </button>
        );
      })}
      <button
        type="button"
        onClick={openMore}
        className="flex-1 flex flex-col items-center justify-center gap-1 text-2xs font-medium text-ink-500"
      >
        <FontAwesomeIcon icon={faBars} className="text-lg" />
        <span>More</span>
      </button>
    </nav>
  );
}
